import { useState } from 'react'
import { toast } from 'sonner'
import { RefreshCw, Loader2, Link } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useTwilioNumbers,
  useAttachTwilioNumber,
  type TwilioNumber,
  type NumberType,
} from '@/entities/twilioNumber'
import { ConfigureDialog } from './ConfigureDialog'

const TYPE_BADGE: Record<NumberType, { label: string; className: string }> = {
  twiml: { label: 'Attached', className: 'bg-green-100 text-green-800 border-green-200' },
  vapi: { label: 'VAPI', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  sip_trunk: { label: 'SIP Trunk', className: 'bg-purple-100 text-purple-800 border-purple-200' },
  unattached: { label: 'Other', className: 'bg-gray-100 text-gray-600 border-gray-200' },
}

export function TwilioNumbersPage() {
  const { data: numbers, isLoading, isFetching, refetch } = useTwilioNumbers()
  const attach = useAttachTwilioNumber()

  const [attachingSid, setAttachingSid] = useState<string | null>(null)
  const [selectedNumber, setSelectedNumber] = useState<TwilioNumber | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  function handleAttach(number: TwilioNumber) {
    setAttachingSid(number.sid)
    attach.mutate(number.sid, {
      onSuccess: () => {
        toast.success(`Attached ${number.phoneNumber} to our webhook`)
        setAttachingSid(null)
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to attach number')
        setAttachingSid(null)
      },
    })
  }

  function handleRowClick(number: TwilioNumber) {
    if (number.type !== 'twiml') return
    setSelectedNumber(number)
    setDialogOpen(true)
  }

  const sorted = numbers?.slice().sort((a, b) => {
    const order: Record<NumberType, number> = { twiml: 0, vapi: 1, sip_trunk: 2, unattached: 3 }
    return order[a.type] - order[b.type]
  })

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-heading font-bold">Twilio Phone Numbers</h1>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Phone Number</TableHead>
                <TableHead>Friendly Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>SIP Address</TableHead>
                <TableHead>Campaign ID</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted?.map((n) => {
                const badge = TYPE_BADGE[n.type]
                const isAttaching = attachingSid === n.sid
                return (
                  <TableRow
                    key={n.sid}
                    className={n.type === 'twiml' ? 'cursor-pointer hover:bg-muted/50' : ''}
                    onClick={() => handleRowClick(n)}
                  >
                    <TableCell className="font-mono text-detail">{n.phoneNumber}</TableCell>
                    <TableCell className="text-detail">{n.friendlyName}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={badge.className}>
                        {badge.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-detail font-mono">
                      {n.config?.sipAddress || '—'}
                    </TableCell>
                    <TableCell className="text-detail">
                      {n.config?.campaignId || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {n.type !== 'twiml' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleAttach(n)
                          }}
                          disabled={isAttaching}
                        >
                          {isAttaching ? (
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          ) : (
                            <Link className="mr-2 h-3 w-3" />
                          )}
                          Attach
                        </Button>
                      )}
                      {n.type === 'twiml' && (
                        <span className="text-detail text-muted-foreground">Click to edit</span>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
              {sorted?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No phone numbers found in the Twilio account.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfigureDialog
        number={selectedNumber}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  )
}
